import { useCallback, useRef, useState } from 'react';
import { flushSync } from 'react-dom';

const FADE_DURATION = 300;

export const useToolStatus = () => {
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [toolStatusVisible, setToolStatusVisible] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasToolStatusRef = useRef(false);

  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const handleToolStatus = useCallback(
    (status: string | null) => {
      clearFadeTimer();
      if (status === null) {
        hasToolStatusRef.current = false;
        flushSync(() => setToolStatusVisible(false));
        fadeTimerRef.current = setTimeout(() => {
          flushSync(() => setToolStatus(null));
        }, FADE_DURATION);
        return;
      }

      if (hasToolStatusRef.current) {
        flushSync(() => setToolStatusVisible(false));
        fadeTimerRef.current = setTimeout(() => {
          flushSync(() => setToolStatus(status));
          requestAnimationFrame(() => {
            setToolStatusVisible(true);
          });
        }, FADE_DURATION);
      } else {
        hasToolStatusRef.current = true;
        flushSync(() => setToolStatus(status));
        requestAnimationFrame(() => {
          setToolStatusVisible(true);
        });
      }
    },
    [clearFadeTimer],
  );

  const resetToolStatus = useCallback(() => {
    clearFadeTimer();
    setToolStatus(null);
    setToolStatusVisible(false);
    hasToolStatusRef.current = false;
  }, [clearFadeTimer]);

  return { toolStatus, toolStatusVisible, handleToolStatus, clearFadeTimer, resetToolStatus };
};
